// ✨ Stylish Navbar with Dark Mode Toggle + Avatar Dropdown + Working Mobile Menu
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { IoMenu } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { FaShoppingCart, FaChevronDown, FaMoon, FaSun } from "react-icons/fa";
import logo from "../logo.png";
import { useMyContext } from "../store/ContextApi";
import { Menu } from "@headlessui/react";
import Avatar from "@mui/material/Avatar";

const Navbar = () => {
  const [headerToggle, setHeaderToggle] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const pathName = useLocation().pathname;
  const navigate = useNavigate();
  const {
    token,
    setToken,
    setCurrentUser,
    isAdmin,
    setIsAdmin,
    isLearner,
    isInstructor,
    currentUser,
    setIsLearner,
    setIsInstructor,
  } = useMyContext();

  const handleLogout = () => {
    localStorage.clear();
    setToken(null);
    setCurrentUser(null);
    setIsAdmin(false);
    setIsInstructor(false);
    setIsLearner(false);
    navigate("/login");
  };

  const toggleTheme = () => setDarkMode(!darkMode);

  useEffect(() => {
    const publicPaths = [
      "/",
      "/login",
      "/register",
      "/signup",
      "/forgot-password",
      "/reset-password",
      "/about",
      "/contact",
      "/terms",
      "/privacy",
      "/help",
      "/faq",
      "/refunds",
      "/course-detail",
    ];

    const isPublicPath = publicPaths.some((base) => pathName.startsWith(base));

    if (localStorage.getItem("JWT_TOKEN") === null && !isPublicPath) {
      navigate("/");
    }
  }, [token, pathName, navigate]);

  return (
    <header
      className={`sticky top-0 z-50 shadow-md backdrop-blur-lg transition-colors duration-300 ${
        darkMode ? "bg-gray-900 text-white" : "bg-white/30 text-gray-800"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3">
          <img src={logo} alt="Logo" className="h-10 drop-shadow-md" />
          <span
            className={`text-3xl font-extrabold tracking-tight font-mono ${
              darkMode ? "text-yellow-400" : "text-indigo-600"
            }`}
          >
            Vicademics
          </span>
        </Link>

        <ul className="hidden lg:flex items-center gap-6 text-base font-medium">
          <NavItem
            to="/home"
            label="Home"
            pathName={pathName}
          />
          <NavItem
            to="/browse-courses"
            label="Browse Courses"
            pathName={pathName}
          />
          {isLearner && (
            <NavItem
              to="/learner/my-courses"
              label="My Learning"
              pathName={pathName}
            />
          )}
          {/* <NavItem
            to="/instructor/dashboard"
            label="Instructor"
            pathName={pathName}
          /> */}
          <NavItem
            to="/show-my-cart"
            label={
              <span className="flex items-center gap-1">
                <FaShoppingCart className="text-lg" /> Cart
              </span>
            }
            pathName={pathName}
          />

          <button
            onClick={toggleTheme}
            className="text-xl hover:text-yellow-400 transition-colors"
          >
            {darkMode ? <FaSun /> : <FaMoon />}
          </button>

          {token ? (
            <>
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-2 hover:text-indigo-600 transition-colors">
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {currentUser?.name?.[0] || "U"}
                  </Avatar>
                  <FaChevronDown className="text-sm" />
                </Menu.Button>
                <Menu.Items className="absolute right-0 mt-2 w-48 bg-white text-gray-800 rounded shadow-md z-50">
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/profile"
                        className={`block px-4 py-2 ${
                          active ? "bg-indigo-100" : ""
                        }`}
                      >
                        Account Setting
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/profile-detail"
                        className={`block px-4 py-2 ${
                          active ? "bg-indigo-100" : ""
                        }`}
                      >
                        Account Details
                      </Link>
                    )}
                  </Menu.Item>
                  <div className="border-t border-gray-200 my-1"></div>
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/about"
                        className={`block px-4 py-2 ${
                          active ? "bg-indigo-100" : ""
                        }`}
                      >
                        About
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/contact"
                        className={`block px-4 py-2 ${
                          active ? "bg-indigo-100" : ""
                        }`}
                      >
                        Contact
                      </Link>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Menu>

              <button
                onClick={handleLogout}
                className="ml-4 bg-red-500 hover:bg-red-600 px-4 py-2 rounded-full text-white font-semibold transition-all"
              >
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-4 py-2 rounded-full font-semibold hover:shadow-md transition-all"
            >
              Sign In / Sign Up
            </Link>
          )}
        </ul>

        <div
          className="lg:hidden text-3xl cursor-pointer text-gray-800"
          onClick={() => setHeaderToggle(!headerToggle)}
        >
          {headerToggle ? <RxCross2 /> : <IoMenu />}
        </div>
      </nav>

      {headerToggle && (
        <div className="lg:hidden px-6 pb-4 pt-2 space-y-4 text-base bg-white text-gray-900">
          <NavItem
            to="/home"
            label="Home"
            pathName={pathName}
            mobile
            onClick={() => setHeaderToggle(false)}
          />
          <NavItem
            to="/browse-courses"
            label="Browse Courses"
            pathName={pathName}
            mobile
            onClick={() => setHeaderToggle(false)}
          />
          {/* <NavItem
            to="/learner/my-courses"
            label="My Learning"
            pathName={pathName}
            mobile
            onClick={() => setHeaderToggle(false)}
          />
          <NavItem
            to="/instructor/dashboard"
            label="Instructor"
            pathName={pathName}
            mobile
            onClick={() => setHeaderToggle(false)}
          /> */}
          <NavItem
            to="/show-my-cart"
            label={
              <span className="flex items-center gap-1">
                <FaShoppingCart className="text-lg" /> Cart
              </span>
            }
            pathName={pathName}
            mobile
            onClick={() => setHeaderToggle(false)}
          />

          {token ? (
            <div className="space-y-2">
              <NavItem
                to="/profile"
                label="Account Setting"
                pathName={pathName}
                mobile
                onClick={() => setHeaderToggle(false)}
              />
              <NavItem
                to="/profile-detail"
                label="Account Details"
                pathName={pathName}
                mobile
                onClick={() => setHeaderToggle(false)}
              />
              <div className="border-t border-gray-300 my-2"></div>
              <NavItem
                to="/about"
                label="About"
                pathName={pathName}
                mobile
                onClick={() => setHeaderToggle(false)}
              />
              <NavItem
                to="/contact"
                label="Contact"
                pathName={pathName}
                mobile
                onClick={() => setHeaderToggle(false)}
              />
              <button
                onClick={() => {
                  handleLogout();
                  setHeaderToggle(false);
                }}
                className="text-red-500 font-semibold block w-full text-left"
              >
                Logout
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              onClick={() => setHeaderToggle(false)}
              className="block bg-yellow-400 text-black px-4 py-2 rounded-full text-center font-semibold hover:bg-yellow-500 transition-all"
            >
              Sign In / Sign Up
            </Link>
          )}
        </div>
      )}
    </header>
  );
};

const NavItem = ({ to, label, pathName, mobile = false, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`${
      pathName.startsWith(to) ? "text-yellow-400 font-bold" : ""
    } hover:text-yellow-400 transition-colors ${mobile ? "block w-full" : ""}`}
  >
    {label}
  </Link>
);

export default Navbar;
